/**
 * SEO utility helpers for managing <head> meta tags in a React SPA.
 *
 * This module provides:
 *  - setHeadTag: upsert a single <meta> or <link> element and return a cleanup fn
 *  - applyPageSeo: apply a full set of SEO tags for a page and return a cleanup fn
 *  - buildTemplateSeoTitle: canonical title format for template detail pages
 */

const SITE_NAME = "React Email Builder";
const SITE_URL = "https://reactemailbuilder.com";

export type SeoTagConfig = {
  title: string;
  description: string;
  path: string;
  /** Optional OG image URL (absolute). Defaults to /og-image.png if not provided. */
  ogImage?: string;
  /** Additional keywords to append. */
  keywords?: string[];
  /** Twitter card type. Defaults to "summary_large_image". */
  twitterCard?: "summary" | "summary_large_image";
  /** Robots directive. Defaults to "index,follow". */
  robots?: string;
  /** Override the canonical/og URL when it should not follow window.location.origin. */
  canonicalUrl?: string;
  /** When false, keep the exact title instead of appending the site name. */
  appendSiteName?: boolean;
};

type MetaTag =
  | { tag: "meta"; attribute: "name" | "property"; key: string; content: string }
  | { tag: "link"; attribute: "rel"; key: string; href: string };

/** Upsert a single head tag and return a cleanup fn that restores previous state. */
export function setHeadTag(
  tagName: "meta" | "link",
  attribute: "name" | "property" | "rel",
  key: string,
  valueAttribute: "content" | "href",
  value: string,
): () => void {
  const selector = `${tagName}[${attribute}="${key}"]`;
  let element = document.head.querySelector(selector) as HTMLMetaElement | HTMLLinkElement | null;
  const created = !element;
  const previousValue = element?.getAttribute(valueAttribute) ?? null;

  if (!element) {
    element = document.createElement(tagName) as HTMLMetaElement | HTMLLinkElement;
    element.setAttribute(attribute, key);
    document.head.appendChild(element);
  }

  element.setAttribute(valueAttribute, value);

  return () => {
    if (!element) return;
    if (created) {
      element.remove();
      return;
    }
    if (previousValue === null) {
      element.removeAttribute(valueAttribute);
      return;
    }
    element.setAttribute(valueAttribute, previousValue);
  };
}

/**
 * Apply a full set of SEO tags for a page.
 * Returns a cleanup function that restores previous state (for use in useEffect).
 */
export function applyPageSeo(config: SeoTagConfig): () => void {
  const previousTitle = document.title;
  const fullTitle = config.appendSiteName === false ? config.title : `${config.title} | ${SITE_NAME}`;
  const url = config.canonicalUrl ?? `${SITE_URL}${config.path}`;
  const ogImage = config.ogImage ?? `${SITE_URL}/og-image.png`;
  const twitterCard = config.twitterCard ?? "summary_large_image";

  document.title = fullTitle;

  const tags: MetaTag[] = [
    { tag: "meta", attribute: "name", key: "description", content: config.description },
    { tag: "meta", attribute: "name", key: "robots", content: config.robots ?? "index,follow" },
    ...(config.keywords?.length
      ? [{ tag: "meta" as const, attribute: "name" as const, key: "keywords", content: config.keywords.join(", ") }]
      : []),
    // Open Graph
    { tag: "meta", attribute: "property", key: "og:title", content: fullTitle },
    { tag: "meta", attribute: "property", key: "og:description", content: config.description },
    { tag: "meta", attribute: "property", key: "og:type", content: "website" },
    { tag: "meta", attribute: "property", key: "og:url", content: url },
    { tag: "meta", attribute: "property", key: "og:image", content: ogImage },
    { tag: "meta", attribute: "property", key: "og:image:width", content: "1200" },
    { tag: "meta", attribute: "property", key: "og:image:height", content: "630" },
    { tag: "meta", attribute: "property", key: "og:site_name", content: SITE_NAME },
    // Twitter Card
    { tag: "meta", attribute: "name", key: "twitter:card", content: twitterCard },
    { tag: "meta", attribute: "name", key: "twitter:title", content: fullTitle },
    { tag: "meta", attribute: "name", key: "twitter:description", content: config.description },
    { tag: "meta", attribute: "name", key: "twitter:image", content: ogImage },
    // Canonical
    { tag: "link", attribute: "rel", key: "canonical", href: url },
  ];

  const cleanups = tags.map((t) => {
    if (t.tag === "meta") {
      return setHeadTag("meta", t.attribute as "name" | "property", t.key, "content", t.content);
    } else {
      return setHeadTag("link", "rel", t.key, "href", t.href);
    }
  });

  return () => {
    document.title = previousTitle;
    cleanups.forEach((fn) => fn());
  };
}

/**
 * Build canonical title for a template detail page.
 * Format: "[Brand] [Template Title] Email Template | React Email Builder"
 */
export function buildTemplateSeoTitle(brandLabel: string, templateTitle: string): string {
  return `${brandLabel} ${templateTitle} Email Template | ${SITE_NAME}`;
}

export { SITE_NAME, SITE_URL };
